import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Badge, Empty, Field, SectionTitle, Table } from "./_ui";
import {
  CONTRACT_TYPES,
  customerWhatsAppHref,
  fmtDateTime,
  formatCHF,
  serviceTypeLabel,
} from "../../lib/adminUtils";
import { Copy, ExternalLink, Loader2, Mail, MessageCircle, Plus } from "lucide-react";

export default function PaymentsView({ customers, reloadGlobal }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    customerId: "",
    serviceType: "one_time",
    description: "Désinfection vapeur professionnelle",
    amount: "",
    frequencyPerMonth: 2,
    internalNote: "",
  });

  const customer = useMemo(
    () => customers.find((c) => c.id === form.customerId),
    [customers, form.customerId]
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payment-links");
      setPayments(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (key, value) => {
    if (key === "customerId") {
      const c = customers.find((item) => item.id === value);
      setForm((current) => ({
        ...current,
        customerId: value,
        description: c ? `Désinfection vapeur professionnelle — ${c.establishment}` : current.description,
      }));
      return;
    }
    setForm((current) => ({ ...current, [key]: value }));
  };

  const create = async () => {
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/payment-links", {
        customerId: form.customerId,
        serviceType: form.serviceType,
        description: form.description,
        amount: Number(form.amount),
        frequencyPerMonth: form.serviceType === "recurring" ? Number(form.frequencyPerMonth) : null,
        internalNote: form.internalNote,
      });
      setForm((current) => ({ ...current, amount: "", internalNote: "" }));
      await load();
      await reloadGlobal?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de la génération Stripe.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="payments-view">
      <div>
        <SectionTitle>Générer un lien Stripe Checkout</SectionTitle>
        <div className="bg-white border border-slate-200 rounded-md p-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Client">
            <select className="field" value={form.customerId} onChange={(e) => update("customerId", e.target.value)} data-testid="pay-customer">
              <option value="">— Sélectionner —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.establishment} · {c.contactName}</option>
              ))}
            </select>
          </Field>
          <Field label="Type de service">
            <select className="field" value={form.serviceType} onChange={(e) => update("serviceType", e.target.value)} data-testid="pay-type">
              {CONTRACT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Prix CHF">
            <input className="field" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => update("amount", e.target.value)} data-testid="pay-amount" />
          </Field>
          {form.serviceType === "recurring" && (
            <Field label="Fréquence / mois">
              <input className="field" type="number" min="1" step="1" value={form.frequencyPerMonth} onChange={(e) => update("frequencyPerMonth", e.target.value)} data-testid="pay-frequency" />
            </Field>
          )}
          <div className="sm:col-span-2 lg:col-span-4">
            <Field label="Description">
              <input className="field" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="pay-description" />
            </Field>
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <Field label="Note interne">
              <textarea className="field" rows={2} value={form.internalNote} onChange={(e) => update("internalNote", e.target.value)} data-testid="pay-note" />
            </Field>
          </div>
          {customer && (
            <p className="sm:col-span-2 lg:col-span-4 text-xs text-slate-500">
              Stripe utilisera {customer.email || "email manquant"} · {customer.phone || "téléphone manquant"}.
            </p>
          )}
          {error && <p className="sm:col-span-2 lg:col-span-4 text-xs text-red-700">{error}</p>}
          <div className="sm:col-span-2 lg:col-span-4">
            <button onClick={create} disabled={creating || !form.customerId || !form.amount || !form.description} className="btn-primary !text-xs" data-testid="pay-create">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Generate payment link
            </button>
          </div>
        </div>
      </div>

      <div>
        <SectionTitle>Paiements</SectionTitle>
        {loading ? (
          <p className="text-sm text-slate-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…</p>
        ) : payments.length === 0 ? (
          <Empty label="Aucun paiement généré." />
        ) : (
          <Table headers={["Description", "Customer", "Type", "Amount", "Status", "Actions"]} testid="payments-table">
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3">
                  <p className="font-medium text-slate-900">{p.description}</p>
                  <p className="text-xs text-slate-500">{fmtDateTime(p.createdAt)}</p>
                </td>
                <td className="p-3 text-slate-700">{p.customerLabel || "—"}</td>
                <td className="p-3 text-slate-700">{serviceTypeLabel(p.serviceType || p.productType)}</td>
                <td className="p-3 font-semibold">{formatCHF(p.amount)}</td>
                <td className="p-3"><Badge status={p.status} /></td>
                <td className="p-3">
                  <PaymentActions payment={p} />
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function PaymentActions({ payment }) {
  const url = payment.checkoutUrl || payment.url;
  const contactName = payment.contactName || "";
  const waText = `Bonjour ${contactName},\n\nVoici votre lien de paiement SafeMat :\n\n${url}\n\nMerci pour votre confiance.`;
  const emailBody = `Bonjour ${contactName},\n\nVeuillez trouver ci-dessous votre lien de paiement sécurisé :\n\n${url}\n\nMerci pour votre confiance.`;
  const customer = { phone: payment.customerPhone, email: payment.customerEmail };
  const copy = async () => {
    await navigator.clipboard?.writeText(url);
  };

  return (
    <div className="flex flex-wrap gap-1.5">
      <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50">
        <Copy size={11} /> Copy link
      </button>
      <a href={customerWhatsAppHref(customer, waText)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#25D366] text-white rounded">
        <MessageCircle size={11} /> Send by WhatsApp
      </a>
      <a href={`mailto:${payment.customerEmail || ""}?subject=${encodeURIComponent("Votre lien de paiement SafeMat")}&body=${encodeURIComponent(emailBody)}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50">
        <Mail size={11} /> Send by Email
      </a>
      <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50">
        <ExternalLink size={11} /> Open Checkout
      </a>
    </div>
  );
}
